// Lib
import { get, negate } from 'lodash/fp';
import { groupBy } from 'lodash';

// Utils
import { createSmartSelector } from '../../../../utils/milanoteReselect/milanoteReselect';
import { isLocationTrash } from '../../../../../common/elements/utils/elementLocationUtils';
import { isEmpty } from '../../../../../common/utils/immutableHelper';
import { isElementLastModifiedByUser } from '../../../../../common/elements/utils/elementUserUtils';
import { isAnnotation, isLine, isTask } from '../../../../../common/elements/utils/elementTypeUtils';
import { elementIsInterestingForTrash } from './clientTrashUtils';
import { getAsyncResourceEntityState } from '../../../../utils/services/http/asyncResource/asyncResourceSelector';
import {
    isAsyncEntityFetched,
    isAsyncEntityFetching,
} from '../../../../utils/services/http/asyncResource/asyncResourceUtils';

// Selectors
import { getElements } from '../../../../element/selectors/elementsSelector';
import { getElementId, getTrashedDate } from '../../../../../common/elements/utils/elementPropertyUtils';
import { getStartOfDayDate } from '../../../../../common/utils/timeUtil';
import { getCurrentUserId } from '../../../../user/currentUserSelector';
import { elementGraphSelector } from '../../../../element/selectors/elementGraphSelector';
import { threadCommentIdsMapSelector } from '../../../../element/comment/store/commentsSelector';

// Constants
import { TRASH_TABS } from './trashConstants';
import { ResourceTypes } from '../../../../utils/services/http/asyncResource/asyncResourceConstants';

const sortByAddedDate = (elementA, elementB) => {
    const timeDiff = getTrashedDate(elementB) - getTrashedDate(elementA);
    if (timeDiff !== 0) return timeDiff;

    const elementIdA = getElementId(elementA);
    const elementIdB = getElementId(elementB);

    return elementIdA < elementIdB ? 1 : -1;
};
const sortByTimestamp = (groupA, groupB) => get('timestamp', groupB) - get('timestamp', groupA);

export const trashElementsSelector = createSmartSelector(getElements, (elements) => elements.filter(isLocationTrash));

const filteredTrashElementsSelector = createSmartSelector(trashElementsSelector, (trashElements) =>
    trashElements.filter((element) => !isLine(element) && !isTask(element) && !isAnnotation(element)),
);

export const filteredTrashElementIdsSelector = createSmartSelector(filteredTrashElementsSelector, (trashElements) =>
    trashElements.map(getElementId).toArray(),
);

export const trashedByCurrentUserElementsSelector = createSmartSelector(
    filteredTrashElementsSelector,
    getCurrentUserId,
    (trashElements, currentUserId) => {
        const isTrashedByCurrentUser = isElementLastModifiedByUser(currentUserId);
        return trashElements.filter(isTrashedByCurrentUser);
    },
);

export const trashedByCurrentUserElementIdsSelector = createSmartSelector(
    trashedByCurrentUserElementsSelector,
    (trashElements) => trashElements.map(getElementId).toArray(),
);

export const emptyCurrentUserTrashSelector = createSmartSelector(trashedByCurrentUserElementsSelector, isEmpty);

/**
 * This list will filter empty elements on the client side as well.
 */
export const trashedByOtherUsersElementsSelector = createSmartSelector(
    filteredTrashElementsSelector,
    getCurrentUserId,
    elementGraphSelector,
    threadCommentIdsMapSelector,
    (trashElements, currentUserId, elementGraph, commentsByThreadId) => {
        const isTrashedByOtherUsers = negate(isElementLastModifiedByUser(currentUserId));
        return trashElements.filter(
            (element) =>
                isTrashedByOtherUsers(element) &&
                elementIsInterestingForTrash({ element, elementGraph, commentsByThreadId }),
        );
    },
);

export const trashedByOtherUsersElementIdsSelector = createSmartSelector(
    trashedByOtherUsersElementsSelector,
    (trashElements) => trashElements.map(getElementId).toArray(),
);

export const emptyOtherUserTrashSelector = createSmartSelector(trashedByOtherUsersElementsSelector, isEmpty);

const createGroupedTrashElementIdsSelector = (trashElementSelector) =>
    createSmartSelector(trashElementSelector, (trashElements) => {
        const groupedElements = groupBy(trashElements.toArray(), (element) => {
            const trashedDate = getTrashedDate(element);
            const startOfTrashedDate = getStartOfDayDate(trashedDate);
            return startOfTrashedDate.getTime();
        });

        const groups = [];
        for (const timestampStr in groupedElements) {
            if (!groupedElements.hasOwnProperty(timestampStr)) continue;

            const timestamp = parseInt(timestampStr, 10);

            groups.push({
                timestamp,
                elementIds: groupedElements[timestampStr].sort(sortByAddedDate).map(getElementId),
            });
        }

        return groups.sort(sortByTimestamp);
    });

export const trashedByCurrentUserGroupedElementIdsSelector = createGroupedTrashElementIdsSelector(
    trashedByCurrentUserElementsSelector,
);

export const trashedByOtherUsersGroupedElementIdsSelector = createGroupedTrashElementIdsSelector(
    trashedByOtherUsersElementsSelector,
);

// Resource state
export const trashFetchedSelector = (tab) => (state) =>
    isAsyncEntityFetched(getAsyncResourceEntityState(state, ResourceTypes.trash, tab));
export const trashFetchingSelector = (tab) => (state) =>
    isAsyncEntityFetching(getAsyncResourceEntityState(state, ResourceTypes.trash, tab));
export const trashHasMoreSelector = (tab) => (state) =>
    getAsyncResourceEntityState(state, ResourceTypes.trash, tab)?.meta?.hasMore || false;
export const trashHasOlderSelector = (tab) => (state) =>
    getAsyncResourceEntityState(state, ResourceTypes.trash, tab)?.meta?.hasOlderTrash || false;
export const trashEarliestTimestampSelector = (tab) => (state) =>
    getAsyncResourceEntityState(state, ResourceTypes.trash, tab)?.meta?.earliestTimestamp;
export const isClearingTrashSelector = (state) =>
    getAsyncResourceEntityState(state, ResourceTypes.trash, TRASH_TABS.PERSONAL)?.meta?.clearing || false;
